import { supabase } from "../lib/supabase";
import * as profilesRepo from "../lib/repo/profiles";
import * as schoolsRepo from "../lib/repo/schools";
import type { Profile, School } from "../lib/types";

export type SessionStatus = "loading" | "anon" | "authed";

export interface SessionStore {
  status: SessionStatus;
  user: { id: string; email: string } | null;
  profile: Profile | null;
  school: School | null;
  signInPending: boolean;
  signInMessage: string | null;
  init(): Promise<void>;
  signIn(email: string, schoolSlug: string): Promise<{ ok: boolean; error?: string }>;
  signOut(): Promise<void>;
}

export function createSessionStore(): SessionStore {
  return {
    status: "loading",
    user: null,
    profile: null,
    school: null,
    signInPending: false,
    signInMessage: null,

    async init() {
      const { data } = await supabase.auth.getSession();
      const session = data.session;
      if (!session) {
        this.status = "anon";
        return;
      }
      this.user = { id: session.user.id, email: session.user.email ?? "" };
      const profile = await profilesRepo.byAuthUserId(session.user.id);
      if (profile) {
        this.profile = profile;
        if (profile.schoolId) this.school = await schoolsRepo.getSchoolById(profile.schoolId);
      }
      this.status = "authed";

      supabase.auth.onAuthStateChange((_event, s) => {
        if (!s) {
          this.status = "anon";
          this.user = null;
          this.profile = null;
          this.school = null;
        }
      });
    },

    async signIn(email, schoolSlug) {
      this.signInPending = true;
      this.signInMessage = null;
      try {
        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: `${window.location.origin}/`,
            data: { school_slug: schoolSlug },
          },
        });
        if (error) {
          this.signInMessage = error.message;
          return { ok: false, error: error.message };
        }
        this.signInMessage = "Magic link sent. Check your email.";
        return { ok: true };
      } finally {
        this.signInPending = false;
      }
    },

    async signOut() {
      await supabase.auth.signOut();
      this.status = "anon";
      this.user = null;
      this.profile = null;
      this.school = null;
    },
  };
}
